import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, Download, Loader2, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
    { key: "7", label: "Last 7 days", days: 7 },
    { key: "30", label: "Last 30 days", days: 30 },
    { key: "60", label: "Last 60 days", days: 60 },
    { key: "90", label: "Last 90 days", days: 90 },
    { key: "365", label: "Last 12 months", days: 365 },
    { key: "custom", label: "Custom range", days: null },
];

function isoDaysAgo(days) {
    const d = new Date();
    d.setDate(d.getDate() - (days - 1));
    return d.toISOString().slice(0, 10);
}
function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

/**
 * Customer Reports — pick a plant + date range, preview KPIs, download PDF.
 * Uses POST /api/reports/preview and /api/reports/pdf with the new
 * start_date/end_date parameters. Weather is intentionally skipped for
 * customer-generated reports (server side).
 */
export default function Reports() {
    const [plants, setPlants] = useState([]);
    const [loadingPlants, setLoadingPlants] = useState(true);
    const [stationId, setStationId] = useState(null);

    const [preset, setPreset] = useState("30");
    const [startDate, setStartDate] = useState(isoDaysAgo(30));
    const [endDate, setEndDate] = useState(todayIso());
    const today = todayIso();

    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        api.get("/customer/plants")
            .then(r => {
                setPlants(r.data || []);
                setStationId(r.data?.[0]?.id || null);
            })
            .finally(() => setLoadingPlants(false));
    }, []);

    // Update start/end when preset changes (except when user picks 'custom')
    useEffect(() => {
        if (preset === "custom") return;
        const p = PRESETS.find(x => x.key === preset);
        if (p?.days) {
            setStartDate(isoDaysAgo(p.days));
            setEndDate(today);
        }
    }, [preset, today]);

    const selectedPlant = useMemo(() => plants.find(p => p.id === stationId) || plants[0], [plants, stationId]);
    const rangeDays = useMemo(() => {
        try {
            const s = new Date(startDate);
            const e = new Date(endDate);
            return Math.max(1, Math.round((e - s) / 86400000) + 1);
        } catch { return 0; }
    }, [startDate, endDate]);
    const rangeValid = startDate && endDate && startDate <= endDate && rangeDays > 0 && rangeDays <= 400;

    const loadPreview = useCallback(async () => {
        if (!selectedPlant || !rangeValid) return;
        setLoadingPreview(true);
        try {
            const { data } = await api.post("/reports/preview", {
                station_id: selectedPlant.id,
                days: rangeDays,
                start_date: startDate,
                end_date: endDate,
            });
            setPreview(data);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || "Preview failed");
        } finally { setLoadingPreview(false); }
    }, [selectedPlant, rangeValid, rangeDays, startDate, endDate]);

    // Auto-load preview when plant or range changes and is valid
    useEffect(() => {
        if (selectedPlant && rangeValid) loadPreview();
    }, [selectedPlant, rangeValid, loadPreview]);

    const downloadPdf = async () => {
        if (!selectedPlant || !rangeValid) return;
        setDownloading(true);
        try {
            const res = await fetch(`${API}/reports/pdf`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    station_id: selectedPlant.id,
                    days: rangeDays,
                    start_date: startDate,
                    end_date: endDate,
                }),
            });
            if (!res.ok) {
                let detail = "Report generation failed";
                try { detail = (await res.json()).detail || detail; } catch (_) { /* ignore parse error */ }
                throw new Error(detail);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const nameSafe = (selectedPlant.customer_name || selectedPlant.name).replace(/[^a-z0-9]/gi, "_");
            a.href = url;
            a.download = `RPPL_${nameSafe}_${startDate}_to_${endDate}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Report downloaded");
        } catch (err) {
            toast.error(err.message || "Download failed");
        } finally { setDownloading(false); }
    };

    if (loadingPlants) return <div className="text-zinc-500 text-sm">Loading…</div>;
    if (plants.length === 0) {
        return (
            <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center" data-testid="cust-no-plants">
                <FileText className="w-8 h-8 mx-auto text-zinc-300 mb-3" />
                <div className="text-sm text-zinc-600 font-medium">No plants linked to your account yet.</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="cust-reports-page">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Reports</div>
                    <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 mt-1">Performance report on demand</h1>
                    <div className="text-sm text-zinc-500 mt-1">Pick any window — we'll build a fresh PDF with your generation, uptime and inverter details.</div>
                </div>
            </div>

            {/* Controls */}
            <div className="bg-white border border-zinc-200 rounded-sm p-5 space-y-5" data-testid="cust-reports-controls">
                {/* Plant picker */}
                {plants.length > 1 && (
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Plant</label>
                        <div className="flex flex-wrap gap-2">
                            {plants.map((p, i) => {
                                const active = p.id === selectedPlant?.id;
                                return (
                                    <button
                                        key={p.id}
                                        onClick={() => setStationId(p.id)}
                                        className={`px-3 py-2 rounded-sm border text-left transition-colors ${active ? "bg-[#0B0D11] text-white border-[#0B0D11]" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}
                                        data-testid={`cust-reports-plant-${i}`}
                                    >
                                        <div className="text-sm font-medium">{p.customer_name || p.name}</div>
                                        <div className={`text-[10px] font-mono mt-0.5 ${active ? "text-zinc-300" : "text-zinc-500"}`}>
                                            {p.location} · {p.installed_capacity_kw} kWp
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {selectedPlant && (
                    <div className="flex items-center gap-2 text-xs text-zinc-600">
                        <MapPin className="w-3.5 h-3.5 text-zinc-400" />
                        <span className="font-medium text-zinc-800">{selectedPlant.customer_name || selectedPlant.name}</span>
                        <span className="text-zinc-400">·</span>
                        <span className="font-mono">{selectedPlant.location} · {selectedPlant.installed_capacity_kw} kWp</span>
                    </div>
                )}

                {/* Range selector */}
                <div>
                    <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Period</label>
                    <div className="flex flex-wrap gap-1.5">
                        {PRESETS.map(p => (
                            <button
                                key={p.key}
                                onClick={() => setPreset(p.key)}
                                data-testid={`cust-reports-preset-${p.key}`}
                                className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border transition-colors ${preset === p.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Date inputs — always visible so customer can fine-tune */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> From
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={e => { setStartDate(e.target.value); setPreset("custom"); }}
                            max={endDate}
                            className="w-full h-10 rounded-sm border border-zinc-200 px-3 text-sm bg-white font-mono"
                            data-testid="cust-reports-start-date"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> To
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={e => { setEndDate(e.target.value); setPreset("custom"); }}
                            min={startDate}
                            max={today}
                            className="w-full h-10 rounded-sm border border-zinc-200 px-3 text-sm bg-white font-mono"
                            data-testid="cust-reports-end-date"
                        />
                    </div>
                </div>

                <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-zinc-100">
                    <div className="text-xs text-zinc-500 font-mono">
                        {rangeValid ? (
                            <>Selected: <span className="text-zinc-800 font-medium">{rangeDays}</span> day{rangeDays !== 1 ? "s" : ""} · {startDate} → {endDate}</>
                        ) : (
                            <span className="text-red-600">Invalid range — pick a start date on or before the end date.</span>
                        )}
                    </div>
                    <Button
                        onClick={downloadPdf}
                        disabled={!rangeValid || downloading}
                        className="bg-[#0A0A0A] hover:bg-black text-white rounded-full h-10 px-5"
                        data-testid="cust-reports-download-btn"
                    >
                        {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Download PDF
                    </Button>
                </div>
            </div>

            {/* Preview */}
            <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="cust-reports-preview">
                {loadingPreview ? (
                    <div className="text-sm text-zinc-500">Preparing preview…</div>
                ) : !preview ? (
                    <div className="text-sm text-zinc-500">Preview will appear once a valid range is chosen.</div>
                ) : (
                    <>
                        <div className="border-b-2 border-[#0A0A0A] pb-3 mb-4">
                            <div className="text-2xl font-bold text-[#0A0A0A] tracking-wider">RPPL <span className="text-[#16A34A]">SOLAR</span></div>
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Performance Report Preview</div>
                        </div>
                        <div className="mb-4">
                            <div className="text-lg font-semibold text-zinc-900">{preview.plant.customer_name || preview.plant.name}</div>
                            <div className="text-xs text-zinc-500 mt-0.5">{preview.plant.address}</div>
                            <div className="text-xs font-mono text-zinc-500 mt-1">
                                Period: {preview.period.start} → {preview.period.end} · {preview.period.days} days
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <MetricBox label="Total Generation" value={preview.summary.total_generation_kwh?.toLocaleString()} unit="kWh" accent="green" />
                            <MetricBox label="Average Daily" value={preview.summary.average_daily_kwh?.toLocaleString()} unit="kWh" />
                            <MetricBox label="CO₂ Avoided" value={preview.summary.co2_avoided_kg?.toLocaleString()} unit="kg" accent="green" />
                            <MetricBox label="Specific Yield" value={preview.summary.specific_yield_kwh_per_kw} unit="kWh/kWp" />
                            <MetricBox label="Capacity" value={preview.plant.capacity_kw} unit="kWp" />
                            <MetricBox
                                label="System Uptime"
                                value={preview.summary.uptime_pct != null ? preview.summary.uptime_pct : "—"}
                                unit={preview.summary.uptime_pct != null ? "%" : ""}
                                accent={preview.summary.uptime_pct >= 90 ? "green" : preview.summary.uptime_pct >= 70 ? "orange" : undefined}
                            />
                        </div>
                        <div className="mt-4 text-[10px] text-zinc-400 font-mono border-t border-zinc-100 pt-3">
                            Weather data is not included in customer reports.
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function MetricBox({ label, value, unit, accent = "black" }) {
    const c = { orange: "text-[#0A0A0A]", green: "text-[#16A34A]", black: "text-zinc-900" }[accent];
    return (
        <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3">
            <div className="text-[9px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
            <div className="mt-1 flex items-baseline gap-1">
                <span className={`text-lg font-medium font-mono ${c}`}>{value}</span>
                {unit && <span className="text-[10px] font-mono text-zinc-500">{unit}</span>}
            </div>
        </div>
    );
}
